import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { 
  BookOpen, Users, Upload, LogOut, Plus, Search, Edit, Trash2, 
  Eye, EyeOff, Loader2, Check, X, ExternalLink, Send, DollarSign,
  FileText, Image as ImageIcon, LayoutDashboard, KeyRound, Ban, CheckCircle2, History, Wallet, Lock
} from 'lucide-react';
import axios from 'axios';
import { HeaderImageCropper, CoverImageCropper } from './HeaderImageCropper';
import { CustomPaletteEditor, PageBackgroundPicker } from './CustomPaletteEditor';
import { CategoryInput } from './CategoryInput';
import { EditorFinance } from './EditorFinance';
import { EditorPasswordModal } from './EditorPasswordModal';
import { ReadingHistoryModal } from './ReadingHistoryModal';
import { ReportsList } from './ReportsList';
import { LanguageSelect, DEFAULT_LANGUAGE } from './LanguageSelect';
import { AuthorsInput } from './AuthorsInput';
import { FichaAutofill } from './FichaAutofill';
import { splitAuthors } from './CatalogFacets';
import { CountBadge } from './CountBadge';
import { Flag } from 'lucide-react';
import { TrialBanner, TrialExpiredScreen, PlanStatus } from './TrialBanner';
import { RightsDeclaration } from './RightsDeclaration';
import { PlanCheckout } from './PlanCheckout';
import { SimpleModal } from './SimpleModal';
import { CollectionsManager } from './CollectionsManager';
import { CollectionsPicker } from './Collections';
import { Layers, Headphones } from 'lucide-react';
import { AudiobookModal } from './AudiobookModal';
import { SubscribersTable } from './SubscribersTable';
import { EditorProfileCard } from './EditorProfileCard';
import { DashboardAdultCard } from './EditorFinance';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "/livrum-logo-light.webp";

const isLightHex = (hex) => {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > 170;
};

const EditorDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeMenu, setActiveMenu] = useState(() => (new URLSearchParams(window.location.search).get('secao') === 'financeiro' ? 'finance' : 'dashboard'));
  const [unreadReports, setUnreadReports] = useState(0);
  useEffect(() => {
    axios.get(`${API}/digital/reports/unread-count`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setUnreadReports(r.data.count)).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  
  // Books state
  const [books, setBooks] = useState([]);
  const [bookSearch, setBookSearch] = useState('');
  const foldText = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const shownBooks = bookSearch.trim()
    ? books.filter((b) => foldText([b.titulo, b.autor, b.isbn, b.issn, b.categoria].join(' ')).includes(foldText(bookSearch.trim())))
    : books;
  const [showBookForm, setShowBookForm] = useState(false);
  const [audioBook, setAudioBook] = useState(null);
  const [editingBook, setEditingBook] = useState(null);
  const [bookForm, setBookForm] = useState({
    titulo: '', autor: '', descricao: '', isbn: '', editora: '', categoria: '', ano_publicacao: '', edicao: '', paginas: '', idioma: DEFAULT_LANGUAGE
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCover, setSelectedCover] = useState(null);
  const [coverCropSource, setCoverCropSource] = useState(null);
  const [recropLoading, setRecropLoading] = useState(false);

  const recropCurrentCover = async () => {
    const url = editingBook.capa_url.startsWith('http') ? editingBook.capa_url : `${BACKEND_URL}${editingBook.capa_url}`;
    setRecropLoading(true);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('cover');
      const blob = await res.blob();
      setCoverCropSource(new File([blob], 'capa-atual.png', { type: blob.type || 'image/png' }));
    } catch (e) {
      alert('Não foi possível carregar a capa atual. Envie a imagem novamente.');
    } finally {
      setRecropLoading(false);
    }
  };
  const [uploading, setUploading] = useState(false);
  
  // Subscribers state
  const [subscribers, setSubscribers] = useState([]);
  const [showSubscriberForm, setShowSubscriberForm] = useState(false);
  const [exemptUsage, setExemptUsage] = useState({ used: 0, limit: 10 });
  const [subscriberForm, setSubscriberForm] = useState({
    nome: '', email: '', cpf: '', telefone: '', senha: ''
  });

  // Settings state
  const [settings, setSettings] = useState({
    banner_url: null,
    header_image_url: null,
    biblioteca_nome: '',
    biblioteca_descricao: '',
    theme: {
      primary_color: '#68AAB0',
      secondary_color: '#1a202c',
      accent_color: '#68AAB0',
      text_color: '#2D3748',
      palette_id: 'neutral',
      palette_accent: 'neutral-teal'
    }
  });
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [selectedHeaderImage, setSelectedHeaderImage] = useState(null);
  const [cropSource, setCropSource] = useState(null);
  const [historySubscriber, setHistorySubscriber] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [trialBlocked, setTrialBlocked] = useState(null);
  const [rightsAccepted, setRightsAccepted] = useState(false);
  const [collections, setCollections] = useState([]);
  const loadCollections = () => axios.get(`${API}/digital/collections`, { headers: getAuthHeaders() }).then(({ data }) => setCollections(data.collections)).catch(() => {});
  useEffect(() => { loadCollections(); }, []);
  const [showPlan, setShowPlan] = useState(false);

  const getToken = () => localStorage.getItem('token');
  
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${getToken()}`
  });

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
      if (response.data.user.libraryType !== 'digital') {
        navigate('/dashboard');
        return;
      }
      setUser(response.data.user);
    } catch (err) {
      if (err.response?.status === 403) { setTrialBlocked(err.response.data?.detail || 'Conta suspensa.'); return; }
      if (window.location.pathname.startsWith('/editor/dashboard')) {
        navigate(window.location.search.includes('secao=') ? `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}` : '/login');
      }
    }
  }, [navigate]);

  const fetchBooks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/digital/books`, { headers: getAuthHeaders() });
      setBooks(response.data.books || []);
    } catch (err) {
      console.error('Error fetching books:', err);
    }
  }, []);

  const fetchSubscribers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/digital/subscribers`, { headers: getAuthHeaders() });
      setSubscribers(response.data.subscribers || []);
      setExemptUsage({ used: response.data.exempt_used ?? 0, limit: response.data.exempt_limit ?? 10 });
    } catch (err) {
      console.error('Error fetching subscribers:', err);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/digital/settings`, { headers: getAuthHeaders() });
      setSettings({
        banner_url: response.data.banner_url,
        header_image_url: response.data.header_image_url,
        slug: response.data.slug,
        biblioteca_nome: response.data.biblioteca_nome || '',
        biblioteca_descricao: response.data.biblioteca_descricao || '',
        theme: response.data.theme || {
          primary_color: '#68AAB0',
          secondary_color: '#1a202c',
          accent_color: '#68AAB0',
          text_color: '#2D3748',
          palette_id: 'neutral',
          palette_accent: 'neutral-teal'
        }
      });
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      await fetchUser();
      await fetchBooks();
      await fetchSubscribers();
      await fetchSettings();
      setLoading(false);
    };
    init();
  }, [fetchUser, fetchBooks, fetchSubscribers, fetchSettings]);

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('panelLastActivity');
    navigate('/login');
  };

  const handleUploadBook = async (e) => {
    e.preventDefault();
    if (!bookForm.autor.trim()) { alert('Inclua pelo menos um autor'); return; }
    if (!selectedFile) {
      alert('Selecione um arquivo PDF');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const params = new URLSearchParams({
        titulo: bookForm.titulo,
        autor: bookForm.autor,
        descricao: bookForm.descricao || '',
        isbn: bookForm.isbn || '',
        editora: bookForm.editora || '',
        categoria: bookForm.categoria || '',
        ano_publicacao: bookForm.ano_publicacao || '',
        edicao: bookForm.edicao || '',
        paginas: bookForm.paginas ? String(bookForm.paginas) : '',
        idioma: bookForm.idioma || DEFAULT_LANGUAGE,
        colecoes: (bookForm.colecoes || []).join(','),
        rights_declaration: rightsAccepted ? 'true' : 'false'
      });
      
      const bookResponse = await axios.post(`${API}/digital/books?${params.toString()}`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Upload cover if selected
      if (selectedCover && bookResponse.data.book?.id) {
        const coverFormData = new FormData();
        coverFormData.append('file', selectedCover);
        
        await axios.post(`${API}/digital/books/${bookResponse.data.book.id}/cover`, coverFormData, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      alert('Livro adicionado com sucesso!');
      setShowBookForm(false);
      setBookForm({ titulo: '', autor: '', descricao: '', isbn: '', editora: '', categoria: '', ano_publicacao: '', edicao: '', paginas: '', idioma: DEFAULT_LANGUAGE });
      setSelectedFile(null);
      setSelectedCover(null);
      fetchBooks();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao adicionar livro');
    } finally {
      setUploading(false);
    }
  };

  const handleTogglePublish = async (bookId, currentStatus) => {
    try {
      await axios.put(`${API}/digital/books/${bookId}?is_published=${!currentStatus}`, {}, {
        headers: getAuthHeaders()
      });
      fetchBooks();
    } catch (err) {
      alert('Erro ao atualizar livro');
    }
  };

  const handleDeleteBook = async (bookId) => {
    if (!window.confirm('Tem certeza que deseja excluir este livro?')) return;
    try {
      await axios.delete(`${API}/digital/books/${bookId}`, { headers: getAuthHeaders() });
      fetchBooks();
    } catch (err) {
      alert('Erro ao excluir livro');
    }
  };

  const handleToggleVisibility = async (bookId, currentHidden) => {
    try {
      await axios.put(`${API}/digital/books/${bookId}/visibility?hidden=${!currentHidden}`, {}, {
        headers: getAuthHeaders()
      });
      fetchBooks();
    } catch (err) {
      alert(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Erro ao alterar visibilidade do livro');
    }
  };

  const handleEditBook = (book) => {
    setEditingBook(book);
    setBookForm({
      titulo: book.titulo || '',
      autor: book.autor || '',
      descricao: book.descricao || '',
      isbn: book.isbn || '',
      editora: book.editora || user?.bibliotecaNome || '',
      categoria: book.categoria || '',
      ano_publicacao: book.ano_publicacao || '',
      edicao: book.edicao || '',
      paginas: book.paginas || book.page_count || '',
      idioma: book.idioma || DEFAULT_LANGUAGE,
      colecoes: book.colecoes || []
    });
    setRightsAccepted(false);
    setShowBookForm(true);
  };

  const handleUpdateBook = async (e) => {
    e.preventDefault();
    if (!editingBook) return;
    if (!bookForm.autor.trim()) { alert('Inclua pelo menos um autor'); return; }
    
    setUploading(true);
    try {
      const params = new URLSearchParams({
        titulo: bookForm.titulo,
        autor: bookForm.autor,
        descricao: bookForm.descricao || '',
        isbn: bookForm.isbn || '',
        editora: bookForm.editora || '',
        categoria: bookForm.categoria || '',
        ano_publicacao: bookForm.ano_publicacao || '',
        edicao: bookForm.edicao || '',
        paginas: bookForm.paginas ? String(bookForm.paginas) : '',
        idioma: bookForm.idioma || DEFAULT_LANGUAGE,
        colecoes: (bookForm.colecoes || []).join(','),
        rights_declaration: rightsAccepted ? 'true' : 'false'
      });
      
      await axios.put(`${API}/digital/books/${editingBook.id}/metadata?${params.toString()}`, {}, {
        headers: getAuthHeaders()
      });
      
      // Upload new cover if selected
      if (selectedCover) {
        const coverFormData = new FormData();
        coverFormData.append('file', selectedCover);
        
        await axios.post(`${API}/digital/books/${editingBook.id}/cover`, coverFormData, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      alert('Livro atualizado com sucesso!');
      setShowBookForm(false);
      setEditingBook(null);
      setBookForm({ titulo: '', autor: '', descricao: '', isbn: '', editora: '', categoria: '', ano_publicacao: '', edicao: '', paginas: '', idioma: DEFAULT_LANGUAGE });
      setSelectedCover(null);
      fetchBooks();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao atualizar livro');
    } finally {
      setUploading(false);
    }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/digital/subscribers`, {
        ...subscriberForm,
        senha: subscriberForm.senha || null
      }, {
        headers: getAuthHeaders()
      });
      alert('Assinante adicionado com sucesso! Se o leitor já tinha conta Livrum, ele usa a mesma senha.');
      setShowSubscriberForm(false);
      setSubscriberForm({ nome: '', email: '', cpf: '', telefone: '', senha: '' });
      fetchSubscribers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao adicionar assinante');
    }
  };

  const handleChargeSubscriber = async (subscriberId) => {
    try {
      const response = await axios.post(`${API}/digital/subscribers/${subscriberId}/charge`, {}, {
        headers: getAuthHeaders()
      });
      alert(response.data.message || 'E-mail de renovação enviado ao leitor.');
      if (response.data.checkout_url) {
        // Copy to clipboard
        navigator.clipboard.writeText(response.data.checkout_url);
        alert(`Link de pagamento copiado!\n\nEnvie para o assinante:\n${response.data.checkout_url}`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao gerar cobrança');
    }
  };

  const handleUploadBanner = async () => {
    if (!selectedBanner) return;
    
    setSavingSettings(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedBanner);
      
      const response = await axios.post(`${API}/digital/settings/banner`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSettings(prev => ({ ...prev, banner_url: response.data.url }));
      setSelectedBanner(null);
      alert('Banner atualizado com sucesso!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao fazer upload do banner');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleUploadHeaderImage = async () => {
    if (!selectedHeaderImage) return;
    
    setSavingSettings(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedHeaderImage);
      
      const response = await axios.post(`${API}/digital/settings/header-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSettings(prev => ({ ...prev, header_image_url: response.data.url }));
      setSelectedHeaderImage(null);
      alert('Imagem do cabeçalho atualizada com sucesso!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao fazer upload da imagem');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRemoveHeaderImage = async () => {
    if (!confirm('Deseja remover a imagem do cabeçalho? O título e descrição voltarão a aparecer.')) return;
    
    setSavingSettings(true);
    try {
      await axios.delete(`${API}/digital/settings/header-image`, {
        headers: getAuthHeaders()
      });
      
      setSettings(prev => ({ ...prev, header_image_url: null }));
      alert('Imagem do cabeçalho removida!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao remover imagem');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveTheme = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/digital/settings/theme`, settings.theme, {
        headers: getAuthHeaders()
      });
      alert('Tema salvo com sucesso!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao salvar tema');
    } finally {
      setSavingSettings(false);
    }
  };

  // Strip HTML tags and links from text
  const stripHtmlAndLinks = (text) => {
    if (!text) return '';
    // Remove HTML tags
    let clean = text.replace(/<[^>]*>/g, '');
    // Remove URLs
    clean = clean.replace(/https?:\/\/[^\s]+/g, '');
    clean = clean.replace(/www\.[^\s]+/g, '');
    // Remove markdown links
    clean = clean.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    return clean.trim();
  };

  const handleSaveHeaderText = async () => {
    setSavingSettings(true);
    try {
      // Clean the text before saving
      const cleanNome = stripHtmlAndLinks(settings.biblioteca_nome);
      const cleanDescricao = stripHtmlAndLinks(settings.biblioteca_descricao);
      
      const { data } = await axios.put(`${API}/digital/settings/header-text`, {
        biblioteca_nome: cleanNome,
        biblioteca_descricao: cleanDescricao
      }, {
        headers: getAuthHeaders()
      });
      
      setSettings(prev => ({
        ...prev,
        biblioteca_nome: cleanNome,
        biblioteca_descricao: cleanDescricao,
        slug: data.slug || prev.slug
      }));
      setUser(prev => prev ? { ...prev, bibliotecaNome: cleanNome } : prev);
      
      alert('Textos do cabeçalho salvos com sucesso!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao salvar textos');
    } finally {
      setSavingSettings(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  if (trialBlocked) return <TrialExpiredScreen message={trialBlocked} onLogout={handleLogout} token={getToken()} onPaid={() => { setTrialBlocked(null); fetchUser(); }} />;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#68AAB0' }} />
      </div>
    );
  }

  const sidebarStyle = {
    width: '260px',
    background: 'linear-gradient(180deg, #1a1f2e 0%, #2d3748 100%)',
    color: 'white',
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh'
  };

  const menuItemStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '0.5rem',
    background: active ? 'rgba(104, 170, 176, 0.2)' : 'transparent',
    color: active ? '#68AAB0' : '#a0aec0',
    fontWeight: active ? 600 : 400,
    transition: 'all 0.2s ease'
  });

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f7fafc' }}>
      {/* Sidebar */}
      <aside style={sidebarStyle}>
        <div style={{ marginBottom: '2rem' }}>
          <img 
            src={LOGO_URL} 
            alt="Livrum" 
            style={{ height: '36px', width: 'auto', marginBottom: '0.5rem' }} 
          />
          <p style={{ fontSize: '0.85rem', color: '#a0aec0', marginTop: '0.25rem' }}>Painel do Editor</p>
        </div>

        <nav>
          <div style={menuItemStyle(activeMenu === 'dashboard')} onClick={() => setActiveMenu('dashboard')} data-testid="editor-menu-dashboard">
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </div>
          <div style={menuItemStyle(activeMenu === 'books')} onClick={() => setActiveMenu('books')} data-testid="editor-menu-books">
            <BookOpen className="w-5 h-5" />
            Meus Livros
            <span style={{ marginLeft: 'auto', background: '#68AAB0', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '50px', fontSize: '0.75rem' }}>
              {books.length}
            </span>
          </div>
          <div style={menuItemStyle(activeMenu === 'subscribers')} onClick={() => setActiveMenu('subscribers')} data-testid="editor-menu-subscribers">
            <Users className="w-5 h-5" />
            Assinantes
            <span style={{ marginLeft: 'auto', background: '#68AAB0', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '50px', fontSize: '0.75rem' }}>
              {subscribers.length}
            </span>
          </div>
          <div style={menuItemStyle(activeMenu === 'collections')} onClick={() => setActiveMenu('collections')} data-testid="editor-menu-collections">
            <Layers className="w-5 h-5" />
            Coleções
          </div>
          <div style={menuItemStyle(activeMenu === 'settings')} onClick={() => setActiveMenu('settings')} data-testid="editor-menu-settings">
            <ImageIcon className="w-5 h-5" />
            Personalização
          </div>
          <div style={menuItemStyle(activeMenu === 'finance')} onClick={() => setActiveMenu('finance')} data-testid="editor-menu-finance">
            <Wallet className="w-5 h-5" />
            Financeiro
          </div>
          <div style={menuItemStyle(activeMenu === 'reports')} onClick={() => setActiveMenu('reports')} data-testid="editor-menu-reports">
            <Flag className="w-5 h-5" />
            Denúncias
            {unreadReports > 0 && <CountBadge n={unreadReports} testId="editor-reports-badge" />}
          </div>
        </nav>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem', marginTop: '1rem' }} data-testid="editor-sidebar-account">
          <p style={{ fontSize: '0.8rem', color: '#a0aec0', marginBottom: '0.5rem' }}>{user?.bibliotecaNome}</p>
          <Button variant="ghost" onClick={() => setShowPasswordModal(true)} data-testid="editor-change-password-btn"
            style={{ color: '#cbd5e0', width: '100%', justifyContent: 'flex-start', marginBottom: '0.5rem' }}>
            <Lock className="w-4 h-4 mr-2" />
            Alterar senha
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleLogout} 
            data-testid="editor-logout-btn"
            style={{ 
              background: 'white', 
              color: '#1a1f2e',
              width: '100%', 
              justifyContent: 'flex-start',
              fontWeight: 500
            }}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
        {user?.trial?.status === 'active' && <TrialBanner trial={user.trial} onBuy={() => setShowPlan(true)} />}
        {user?.plan?.status && <PlanStatus plan={user.plan} onBuy={() => setShowPlan(true)} />}
        {showPlan && (
          <SimpleModal title="Plano Editora anual" onClose={() => setShowPlan(false)} testId="plan-modal" maxWidth={560}>
            <PlanCheckout token={getToken()} onPaid={fetchUser} onDone={() => { setShowPlan(false); fetchUser(); }} />
          </SimpleModal>
        )}
        {/* Dashboard */}
        {activeMenu === 'dashboard' && (
          <>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2D3748', marginBottom: '1.5rem' }}>
              Bem-vindo, {user?.adminNome?.split(' ')[0]}!
            </h1>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
              <Card style={{ background: 'linear-gradient(135deg, #68AAB0 0%, #8BC4C9 100%)', color: 'white' }}>
                <CardContent style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ opacity: 0.9, marginBottom: '0.25rem' }}>Livros Publicados</p>
                      <p style={{ fontSize: '2rem', fontWeight: 700 }}>{books.filter(b => b.is_published).length}</p>
                    </div>
                    <BookOpen className="w-10 h-10" style={{ opacity: 0.8 }} />
                  </div>
                </CardContent>
              </Card>

              <Card style={{ background: 'linear-gradient(135deg, #6A68A3 0%, #8B89C0 100%)', color: 'white' }}>
                <CardContent style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ opacity: 0.9, marginBottom: '0.25rem' }}>Assinantes Ativos</p>
                      <p style={{ fontSize: '2rem', fontWeight: 700 }}>{subscribers.filter(s => s.status === 'active').length}</p>
                    </div>
                    <Users className="w-10 h-10" style={{ opacity: 0.8 }} />
                  </div>
                </CardContent>
              </Card>

              <Card style={{ background: 'linear-gradient(135deg, #48BB78 0%, #68D391 100%)', color: 'white' }}>
                <CardContent style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ opacity: 0.9, marginBottom: '0.25rem' }}>Valor Assinatura (mensal)</p>
                      <p style={{ fontSize: '2rem', fontWeight: 700 }}>{formatCurrency(user?.valorAssinatura)}</p>
                    </div>
                    <DollarSign className="w-10 h-10" style={{ opacity: 0.8 }} />
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle style={{ color: '#2D3748' }}>Link do seu Catálogo</CardTitle>
              </CardHeader>
              <CardContent>
                <p style={{ marginBottom: '1rem', color: '#718096' }}>
                  Compartilhe este link para que as pessoas possam ver seus livros e assinar:
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Input 
                    readOnly 
                    data-testid="catalog-link-input"
                    value={`${window.location.origin}/${settings.slug || user?.id}`}
                    style={{ flex: 1 }}
                  />
                  <Button 
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/${settings.slug || user?.id}`);
                      alert('Link copiado!');
                    }}
                  >
                    Copiar
                  </Button>
                </div>
              </CardContent>
            </Card>
            <DashboardAdultCard headers={getAuthHeaders()} />
            <EditorProfileCard headers={getAuthHeaders()} onSaved={() => { fetchUser(); fetchSettings(); }} />
          </>
        )}

        {/* Books */}
        {activeMenu === 'books' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2D3748' }}>Meus Livros</h1>
              <Button className="btn-primary-custom" onClick={() => { setRightsAccepted(false); setBookForm((f) => ({ ...f, editora: f.editora || user?.bibliotecaNome || '' })); setShowBookForm(true); }} style={{ background: '#68AAB0' }} data-testid="add-book-btn">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Livro
              </Button>
            </div>

            {/* Upload/Edit Form Modal */}
            {showBookForm && (
              <Card style={{ marginBottom: '1.5rem' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#2D3748' }}>
                    {editingBook ? 'Editar Livro' : 'Novo Livro Digital'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={editingBook ? handleUpdateBook : handleUploadBook}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                      {!editingBook && (
                        <FichaAutofill setForm={setBookForm} authHeaders={getAuthHeaders()}
                          categories={[...new Set(books.map((b) => (b.categoria || '').trim()).filter(Boolean))]} />
                      )}
                      <div>
                        <Label>Título *</Label>
                        <Input 
                          value={bookForm.titulo} 
                          onChange={(e) => setBookForm({...bookForm, titulo: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Autor(es) * <span style={{ fontWeight: 400, color: '#a0aec0', fontSize: '0.75rem' }}>inclua um por vez</span></Label>
                        <AuthorsInput value={bookForm.autor} onChange={(autor) => setBookForm((prev) => ({ ...prev, autor }))}
                          known={[...new Map(books.flatMap((b) => splitAuthors(b.autor)).map((a) => [a.toLowerCase(), a])).values()].sort((a, b) => a.localeCompare(b, 'pt-BR'))} />
                      </div>
                      <div>
                        <Label>Editora</Label>
                        <Input
                          value={bookForm.editora}
                          onChange={(e) => setBookForm({...bookForm, editora: e.target.value})}
                          placeholder={user?.bibliotecaNome || 'Nome da editora'}
                          data-testid="book-form-editora"
                        />
                      </div>
                      <div>
                        <Label>ISBN</Label>
                        <Input 
                          value={bookForm.isbn} 
                          onChange={(e) => setBookForm({...bookForm, isbn: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Edição</Label>
                        <Input value={bookForm.edicao} onChange={(e) => setBookForm({...bookForm, edicao: e.target.value})}
                          placeholder="Ex.: 3ª edição" maxLength={80} data-testid="book-form-edicao" />
                      </div>
                      <div>
                        <Label>Ano de Publicação</Label>
                        <Input 
                          value={bookForm.ano_publicacao} 
                          onChange={(e) => setBookForm({...bookForm, ano_publicacao: e.target.value})}
                        />
                      </div>
                      {editingBook && (
                      <div>
                        <Label>Páginas</Label>
                        <Input
                          type="number" min="1"
                          value={bookForm.paginas}
                          onChange={(e) => setBookForm({...bookForm, paginas: e.target.value})}
                          data-testid="book-form-paginas"
                        />
                      </div>
                      )}
                      <div>
                        <Label>Língua</Label>
                        <LanguageSelect value={bookForm.idioma} onChange={(idioma) => setBookForm((prev) => ({ ...prev, idioma }))} />
                      </div>
                      <div>
                        <Label>Categoria</Label>
                        <CategoryInput
                          value={bookForm.categoria}
                          onChange={(categoria) => setBookForm((prev) => ({ ...prev, categoria }))}
                          categories={[...new Map(books.filter((b) => b.categoria && b.categoria.trim()).map((b) => [b.categoria.trim().toLowerCase(), b.categoria.trim()])).values()].sort((a, b) => a.localeCompare(b, 'pt-BR'))}
                        />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <Label>Coleções</Label>
                        <CollectionsPicker collections={collections} value={bookForm.colecoes || []} onChange={(colecoes) => setBookForm((prev) => ({ ...prev, colecoes }))} />
                      </div>
                      {!editingBook && (
                        <div>
                          <Label>Arquivo PDF *</Label>
                          <Input 
                            type="file" 
                            accept=".pdf,application/pdf"
                            onChange={(e) => setSelectedFile(e.target.files[0])}
                            data-testid="book-pdf-file-input"
                            required
                          />
                          {selectedFile && (
                            <p data-testid="pdf-file-name" style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.25rem' }}>
                              {selectedFile.name} ({formatFileSize(selectedFile.size)}) · páginas contadas automaticamente
                            </p>
                          )}
                        </div>
                      )}
                      <div>
                        <Label>Imagem de Capa {editingBook ? '(nova)' : '(opcional)'}</Label>
                        <Input 
                          type="file" 
                          accept="image/jpeg,image/png,image/webp"
                          onChange={(e) => { if (e.target.files[0]) setCoverCropSource(e.target.files[0]); e.target.value = ''; }}
                          data-testid="book-cover-file-input"
                        />
                        {coverCropSource && (
                          <CoverImageCropper
                            file={coverCropSource}
                            onCancel={() => setCoverCropSource(null)}
                            onConfirm={(cropped) => { setSelectedCover(cropped); setCoverCropSource(null); }}
                          />
                        )}
                        {selectedCover ? (
                          <div style={{ marginTop: '0.5rem' }}>
                            <img 
                              src={URL.createObjectURL(selectedCover)} 
                              alt="Preview da capa" 
                              style={{ 
                                width: '80px', 
                                height: '120px', 
                                objectFit: 'cover', 
                                borderRadius: '4px',
                                border: '1px solid #e2e8f0'
                              }} 
                            />
                            <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.25rem' }}>
                              {selectedCover.name}
                            </p>
                          </div>
                        ) : editingBook && editingBook.capa_url ? (
                          <div style={{ marginTop: '0.5rem' }}>
                            <p style={{ fontSize: '0.75rem', color: '#718096' }}>Capa atual:</p>
                            <img 
                              src={editingBook.capa_url.startsWith('http') ? editingBook.capa_url : `${BACKEND_URL}${editingBook.capa_url}`}
                              alt="Capa atual" 
                              style={{ 
                                width: '80px', 
                                height: '120px', 
                                objectFit: 'cover', 
                                borderRadius: '4px',
                                border: '1px solid #e2e8f0'
                              }} 
                            />
                            <div>
                              <button type="button" onClick={recropCurrentCover} disabled={recropLoading} data-testid="recrop-cover-btn"
                                style={{ marginTop: '0.4rem', background: 'none', border: '1px solid #68AAB0', color: '#2c7a7b', borderRadius: '6px', padding: '0.3rem 0.7rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                                {recropLoading ? 'Carregando...' : 'Recortar capa atual'}
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <Label>Descrição</Label>
                        <textarea 
                          value={bookForm.descricao}
                          onChange={(e) => setBookForm({...bookForm, descricao: e.target.value})}
                          style={{ 
                            width: '100%', 
                            minHeight: '80px', 
                            padding: '0.5rem', 
                            borderRadius: '6px', 
                            border: '1px solid #e2e8f0' 
                          }}
                        />
                      </div>
                    </div>
                    <RightsDeclaration user={user} checked={rightsAccepted} onChange={setRightsAccepted} />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                      <Button type="button" variant="outline" onClick={() => {
                        setShowBookForm(false);
                        setEditingBook(null);
                        setBookForm({ titulo: '', autor: '', descricao: '', isbn: '', editora: '', categoria: '', ano_publicacao: '', edicao: '', paginas: '', idioma: DEFAULT_LANGUAGE });
                        setSelectedCover(null);
                        setSelectedFile(null);
                      }}>
                        Cancelar
                      </Button>
                      <Button type="submit" style={{ background: '#68AAB0' }} disabled={uploading || !rightsAccepted} data-testid="book-form-submit-btn">
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (editingBook ? <Check className="w-4 h-4 mr-2" /> : <Upload className="w-4 h-4 mr-2" />)}
                        {uploading ? 'Salvando...' : (editingBook ? 'Salvar Alterações' : 'Fazer Upload')}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Books Table */}
            <div style={{ position: 'relative', marginBottom: '0.75rem', maxWidth: 480 }}>
              <Search className="w-4 h-4" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }} />
              <Input value={bookSearch} onChange={(e) => setBookSearch(e.target.value)} placeholder="Buscar por título, autor, ISBN/ISSN ou categoria"
                style={{ paddingLeft: 34, paddingRight: 70 }} data-testid="books-search-input" />
              {bookSearch && <span data-testid="books-search-count" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: '0.75rem', color: '#718096' }}>{shownBooks.length} de {books.length}</span>}
            </div>
            <Card>
              <CardContent style={{ padding: 0 }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Capa</TableHead>
                      <TableHead>Título</TableHead>
                      <TableHead>Autor</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Ano</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Visível</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {books.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>
                          Nenhum livro cadastrado. Clique em "Adicionar Livro" para começar.
                        </TableCell>
                      </TableRow>
                    ) : shownBooks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#718096' }} data-testid="books-search-empty">
                          Nenhum livro encontrado para "{bookSearch}".
                        </TableCell>
                      </TableRow>
                    ) : shownBooks.map((book) => (
                      <TableRow key={book.id} style={{ opacity: book.hidden ? 0.6 : 1 }}>
                        <TableCell>
                          <div style={{ 
                            width: '50px', 
                            height: '65px', 
                            background: '#f0f0f0', 
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden'
                          }}>
                            {book.capa_url ? (
                              <img 
                                src={book.capa_url.startsWith('http') ? book.capa_url : `${BACKEND_URL}${book.capa_url}`}
                                alt={book.titulo}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<svg class="w-5 h-5" style="color: #68AAB0"><use href="#book-icon"/></svg>';
                                }}
                              />
                            ) : (
                              <FileText className="w-5 h-5" style={{ color: '#68AAB0' }} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p style={{ fontWeight: 500 }}>{book.titulo}</p>
                            <p style={{ fontSize: '0.8rem', color: '#718096' }}>{book.isbn || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell>{book.autor}</TableCell>
                        <TableCell>{book.categoria || '-'}</TableCell>
                        <TableCell>{book.ano_publicacao || '-'}</TableCell>
                        <TableCell>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '50px',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            background: book.is_published ? '#c6f6d5' : '#fed7d7',
                            color: book.is_published ? '#22543d' : '#9b2c2c'
                          }}>
                            {book.is_published ? 'Publicado' : 'Rascunho'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '50px',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            background: book.hidden ? '#fed7d7' : '#c6f6d5',
                            color: book.hidden ? '#9b2c2c' : '#22543d'
                          }}>
                            {book.admin_blocked ? 'Oculto pelo administrador' : book.hidden ? 'Oculto' : 'Visível'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEditBook(book)}
                              title="Editar"
                              style={{ color: '#68AAB0' }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleToggleVisibility(book.id, book.hidden)}
                              title={book.hidden ? 'Mostrar no catálogo' : 'Ocultar do catálogo'}
                            >
                              {book.hidden ? <Eye className="w-4 h-4 text-green-500" /> : <EyeOff className="w-4 h-4 text-orange-500" />}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setAudioBook(book)}
                              title={book.audiobook?.status === 'ready' ? 'Audiolivro pronto' : 'Gerar audiolivro'}
                              data-testid={`audiobook-btn-${book.id}`}
                            >
                              <Headphones className="w-4 h-4" style={{ color: book.audiobook?.status === 'ready' ? '#2f855a' : '#805ad5' }} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleTogglePublish(book.id, book.is_published)}
                              title={book.is_published ? 'Despublicar' : 'Publicar'}
                            >
                              {book.is_published ? <X className="w-4 h-4 text-red-500" /> : <Check className="w-4 h-4 text-green-500" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}

        {audioBook && <AudiobookModal book={audioBook} token={localStorage.getItem('token')} onClose={() => { setAudioBook(null); fetchBooks(); }} />}

        {/* Subscribers */}
        {activeMenu === 'subscribers' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2D3748' }}>Assinantes</h1>
              <Button
                className="btn-primary-custom"
                onClick={() => setShowSubscriberForm(true)}
                style={{ background: '#68AAB0' }}
                disabled={exemptUsage.used >= exemptUsage.limit}
                title={exemptUsage.used >= exemptUsage.limit ? 'Limite de leitores isentos atingido' : ''}
                data-testid="add-exempt-reader-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Leitor Isento ({exemptUsage.used}/{exemptUsage.limit})
              </Button>
            </div>

            {/* Add Subscriber Form */}
            {showSubscriberForm && (
              <Card style={{ marginBottom: '1.5rem' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#2D3748' }}>Novo Leitor Isento</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddSubscriber}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                      <div>
                        <Label>Nome Completo *</Label>
                        <Input 
                          value={subscriberForm.nome} 
                          onChange={(e) => setSubscriberForm({...subscriberForm, nome: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>E-mail *</Label>
                        <Input 
                          type="email"
                          value={subscriberForm.email} 
                          onChange={(e) => setSubscriberForm({...subscriberForm, email: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>CPF *</Label>
                        <Input 
                          value={subscriberForm.cpf} 
                          onChange={(e) => setSubscriberForm({...subscriberForm, cpf: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Telefone</Label>
                        <Input 
                          value={subscriberForm.telefone} 
                          onChange={(e) => setSubscriberForm({...subscriberForm, telefone: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Senha provisória</Label>
                        <Input 
                          type="password"
                          value={subscriberForm.senha} 
                          onChange={(e) => setSubscriberForm({...subscriberForm, senha: e.target.value})}
                          placeholder="Só para leitor novo; ele troca no 1º acesso"
                          data-testid="subscriber-temp-password-input"
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                      <Button type="button" variant="outline" onClick={() => setShowSubscriberForm(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" style={{ background: '#68AAB0' }}>
                        Adicionar
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            <SubscribersTable subscribers={subscribers} headers={getAuthHeaders()} onHistory={setHistorySubscriber} onChanged={fetchSubscribers} />
          </>
        )}

        {/* Settings */}
        {activeMenu === 'finance' && <EditorFinance headers={getAuthHeaders()} />}
        {activeMenu === 'collections' && <CollectionsManager headers={getAuthHeaders()} onChanged={loadCollections} baseUrl={`${window.location.origin}/${settings.slug || user?.id}`} />}
        {activeMenu === 'reports' && <ReportsList onCountChange={setUnreadReports} />}

        {activeMenu === 'settings' && (
          <>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.5rem', color: '#2D3748' }}>
              Personalização
            </h2>

            {/* Header Configuration */}
            <Card style={{ marginBottom: '1.5rem' }}>
              <CardHeader>
                <CardTitle style={{ fontSize: '1.1rem' }}>Cabeçalho do Catálogo</CardTitle>
              </CardHeader>
              <CardContent>
                <p style={{ color: '#718096', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Configure o que será exibido no topo esquerdo do catálogo público. 
                  Escolha entre texto personalizado ou uma imagem (280 × 100 pixels).
                </p>
                
                {/* Current mode indicator */}
                <div style={{ 
                  marginBottom: '1.5rem', 
                  padding: '0.75rem', 
                  background: settings.header_image_url ? '#ebf8ff' : '#f0fff4', 
                  borderRadius: '8px',
                  border: settings.header_image_url ? '1px solid #90cdf4' : '1px solid #9ae6b4'
                }}>
                  <p style={{ fontSize: '0.9rem', color: settings.header_image_url ? '#2b6cb0' : '#276749', margin: 0 }}>
                    {settings.header_image_url 
                      ? '✓ Exibindo imagem personalizada no cabeçalho' 
                      : '✓ Exibindo título e descrição em texto'}
                  </p>
                </div>
                
                {/* Option 1: Text Fields */}
                {!settings.header_image_url && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f7fafc', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '1rem', fontWeight: 600, color: '#2D3748' }}>
                      Opção 1: Texto Personalizado
                    </h4>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <Label style={{ marginBottom: '0.5rem', display: 'block' }}>
                        Nome da Biblioteca/Editora
                      </Label>
                      <Input
                        value={settings.biblioteca_nome}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          biblioteca_nome: e.target.value
                        }))}
                        placeholder="Ex: Editora Acadêmica"
                        maxLength={100}
                        data-testid="header-nome-input"
                        style={{ maxWidth: '400px' }}
                      />
                      <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.25rem' }}>
                        Apenas texto, sem links ou código HTML
                      </p>
                    </div>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <Label style={{ marginBottom: '0.5rem', display: 'block' }}>
                        Descrição (opcional)
                      </Label>
                      <textarea
                        value={settings.biblioteca_descricao}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          biblioteca_descricao: e.target.value
                        }))}
                        placeholder="Ex: Livros e periódicos para pesquisadores"
                        maxLength={200}
                        rows={2}
                        style={{ 
                          width: '100%', 
                          maxWidth: '400px', 
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0',
                          resize: 'none',
                          fontFamily: 'inherit',
                          fontSize: '0.875rem'
                        }}
                      />
                      <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.25rem' }}>
                        Apenas texto, sem links ou código HTML (máx. 200 caracteres)
                      </p>
                    </div>
                    
                    <Button 
                      onClick={handleSaveHeaderText}
                      disabled={savingSettings}
                      data-testid="save-header-text-btn"
                      style={{ background: '#68AAB0' }}
                    >
                      {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                      Salvar Textos
                    </Button>
                  </div>
                )}
                
                {/* Option 2: Image Upload */}
                <div style={{ padding: '1rem', background: settings.header_image_url ? '#f7fafc' : 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginBottom: '1rem', fontWeight: 600, color: '#2D3748' }}>
                    {settings.header_image_url ? 'Imagem Atual' : 'Opção 2: Imagem Personalizada'}
                  </h4>
                  
                  {/* Preview */}
                  {(settings.header_image_url || selectedHeaderImage) && (
                    <div style={{ marginBottom: '1rem' }}>
                      <Label style={{ marginBottom: '0.5rem', display: 'block' }}>Preview:</Label>
                      <div style={{ 
                        width: '280px', 
                        height: '100px', 
                        boxSizing: 'content-box',
                        background: settings.theme?.secondary_color || '#1a1f2e',
                        border: '2px dashed #e2e8f0',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <img 
                          src={selectedHeaderImage ? URL.createObjectURL(selectedHeaderImage) : `${BACKEND_URL}${settings.header_image_url}`} 
                          alt="Header preview"
                          data-testid="header-image-preview"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Input 
                      type="file" 
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => { if (e.target.files[0]) setCropSource(e.target.files[0]); e.target.value = ''; }}
                      style={{ maxWidth: '300px' }}
                      data-testid="header-image-file-input"
                    />
                    {cropSource && (
                      <HeaderImageCropper
                        file={cropSource}
                        onCancel={() => setCropSource(null)}
                        onConfirm={(file) => { setSelectedHeaderImage(file); setCropSource(null); }}
                      />
                    )}
                    <Button 
                      onClick={handleUploadHeaderImage}
                      disabled={!selectedHeaderImage || savingSettings}
                      data-testid="header-image-upload-btn"
                      style={{ background: '#68AAB0' }}
                    >
                      {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Enviar Imagem
                    </Button>
                    {settings.header_image_url && (
                      <Button 
                        onClick={handleRemoveHeaderImage}
                        disabled={savingSettings}
                        variant="outline"
                        style={{ borderColor: '#e53e3e', color: '#e53e3e' }}
                      >
                        Remover e Usar Texto
                      </Button>
                    )}
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.5rem' }}>
                    Tamanho: 280 × 100 pixels — ao escolher a imagem, arraste e dê zoom para ajustar o recorte. Formatos: JPG, PNG ou WebP. Máx. 2MB
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Color Theme */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '1.1rem' }}>Esquema de Cores</CardTitle>
              </CardHeader>
              <CardContent>
                <p style={{ color: '#718096', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                  Escolha uma das paletas de cores para personalizar sua biblioteca digital.
                </p>
                
                {/* Color Palettes */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <Label style={{ marginBottom: '0.75rem', display: 'block', fontWeight: 600 }}>
                    Paletas Disponíveis
                  </Label>
                  
                  {/* Neutral Palette (Default) */}
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.5rem', fontWeight: 500 }}>
                      ⭐ Padrão Neutro (escolha 1 cor de destaque)
                    </p>
                    <div style={{ 
                      display: 'flex', 
                      gap: '0.5rem', 
                      flexWrap: 'wrap',
                      padding: '0.75rem',
                      background: '#f7fafc',
                      borderRadius: '8px',
                      border: settings.theme.palette_id === 'neutral' ? '2px solid #68AAB0' : '1px solid #e2e8f0'
                    }}>
                      {[
                        { id: 'neutral-teal', accent: '#68AAB0', name: 'Verde-Água' },
                        { id: 'neutral-blue', accent: '#3182CE', name: 'Azul' },
                        { id: 'neutral-green', accent: '#38A169', name: 'Verde' },
                        { id: 'neutral-purple', accent: '#805AD5', name: 'Roxo' },
                        { id: 'neutral-orange', accent: '#DD6B20', name: 'Laranja' },
                        { id: 'neutral-red', accent: '#E53E3E', name: 'Vermelho' },
                        { id: 'neutral-pink', accent: '#D53F8C', name: 'Rosa' },
                        { id: 'neutral-yellow', accent: '#D69E2E', name: 'Amarelo' },
                      ].map((option) => (
                        <button
                          key={option.id}
                          onClick={() => {
                            setSettings(prev => ({
                              ...prev,
                              theme: {
                                primary_color: option.accent,
                                secondary_color: '#1a202c',
                                accent_color: option.accent,
                                text_color: '#2D3748',
                                palette_id: 'neutral',
                                palette_accent: option.id
                              }
                            }));
                          }}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.5rem',
                            background: settings.theme.palette_accent === option.id ? '#e6fffa' : 'white',
                            border: settings.theme.palette_accent === option.id ? `2px solid ${option.accent}` : '1px solid #e2e8f0',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            minWidth: '70px'
                          }}
                          title={option.name}
                        >
                          <div style={{ display: 'flex', gap: '2px' }}>
                            <div style={{ width: '16px', height: '16px', background: '#1a202c', borderRadius: '2px' }} />
                            <div style={{ width: '16px', height: '16px', background: '#ffffff', borderRadius: '2px', border: '1px solid #e2e8f0' }} />
                            <div style={{ width: '16px', height: '16px', background: option.accent, borderRadius: '2px' }} />
                          </div>
                          <span style={{ fontSize: '0.7rem', color: '#4a5568' }}>{option.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Predefined Pantone-style Palettes */}
                  <p style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Paletas Temáticas
                  </p>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: '0.75rem'
                  }}>
                    {[
                      { id: 'ocean', name: 'Oceano', primary: '#0077B6', secondary: '#023E8A', accent: '#00B4D8' },
                      { id: 'forest', name: 'Floresta', primary: '#2D6A4F', secondary: '#1B4332', accent: '#52B788' },
                      { id: 'sunset', name: 'Pôr do Sol', primary: '#E07A5F', secondary: '#3D405B', accent: '#F4A261' },
                      { id: 'lavender', name: 'Lavanda', primary: '#7B68EE', secondary: '#483D8B', accent: '#9370DB' },
                      { id: 'coral', name: 'Coral', primary: '#FF6B6B', secondary: '#2C3E50', accent: '#FF8E72' },
                      { id: 'mint', name: 'Menta', primary: '#26A69A', secondary: '#00695C', accent: '#4DB6AC' },
                      { id: 'wine', name: 'Vinho', primary: '#722F37', secondary: '#2C1320', accent: '#A23B48' },
                      { id: 'gold', name: 'Ouro', primary: '#B8860B', secondary: '#1C1C1C', accent: '#DAA520' },
                      { id: 'navy', name: 'Marinho', primary: '#1E3A5F', secondary: '#0D1B2A', accent: '#3D5A80' },
                      { id: 'terracotta', name: 'Terracota', primary: '#C66B3D', secondary: '#5D4E37', accent: '#E07B53' },
                    ].map((palette) => (
                      <button
                        key={palette.id}
                        onClick={() => {
                          setSettings(prev => ({
                            ...prev,
                            theme: {
                              primary_color: palette.primary,
                              secondary_color: palette.secondary,
                              accent_color: palette.accent,
                              text_color: '#2D3748',
                              palette_id: palette.id
                            }
                          }));
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.75rem',
                          background: settings.theme.palette_id === palette.id ? '#f0fff4' : 'white',
                          border: settings.theme.palette_id === palette.id ? `2px solid ${palette.primary}` : '1px solid #e2e8f0',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div style={{ display: 'flex', gap: '3px' }}>
                          <div style={{ width: '24px', height: '24px', background: palette.secondary, borderRadius: '4px' }} />
                          <div style={{ width: '24px', height: '24px', background: palette.primary, borderRadius: '4px' }} />
                          <div style={{ width: '24px', height: '24px', background: palette.accent, borderRadius: '4px' }} />
                        </div>
                        <span style={{ fontSize: '0.8rem', color: '#4a5568', fontWeight: 500 }}>{palette.name}</span>
                      </button>
                    ))}
                  </div>
                  <CustomPaletteEditor theme={settings.theme} onChange={(theme) => setSettings(prev => ({ ...prev, theme }))} />
                  <PageBackgroundPicker theme={settings.theme} onChange={(theme) => setSettings(prev => ({ ...prev, theme }))} />
                </div>

                {/* Preview */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <Label style={{ marginBottom: '0.75rem', display: 'block' }}>Preview:</Label>
                  <div data-testid="theme-preview" style={{ borderRadius: '12px', overflow: 'hidden', maxWidth: '500px', border: '1px solid #e2e8f0' }}>
                    <div style={{ background: settings.theme.secondary_color, padding: '1.25rem', color: isLightHex(settings.theme.secondary_color) ? '#1a202c' : 'white' }}>
                      <h3 style={{ marginBottom: '0.25rem' }}>Sua Biblioteca Digital</h3>
                      <p style={{ opacity: 0.75, fontSize: '0.9rem' }}>Subtítulo da biblioteca</p>
                    </div>
                    <div style={{ background: settings.theme.page_bg_color || '#f7fafc', padding: '1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <div style={{ width: 44, height: 62, background: 'white', borderRadius: 4, boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }} />
                      <Button style={{ background: settings.theme.primary_color }}>Botão Primário</Button>
                      <Button style={{ background: settings.theme.accent_color }}>Destaque</Button>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleSaveTheme}
                  disabled={savingSettings}
                  style={{ background: '#68AAB0' }}
                >
                  {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Salvar Paleta
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </main>
      {showPasswordModal && <EditorPasswordModal headers={getAuthHeaders()} onClose={() => setShowPasswordModal(false)} />}
      {historySubscriber && (
        <ReadingHistoryModal
          url={`${API}/digital/subscribers/${historySubscriber.id}/history`}
          headers={getAuthHeaders()}
          title={`Histórico de ${historySubscriber.nome}`}
          thirdPerson
          onClose={() => setHistorySubscriber(null)}
        />
      )}
    </div>
  );
};

export default EditorDashboard;
